import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Scale, Play, Pause, Flame } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api, { timeAgo } from "@/lib/api";
import { Article, Regulation } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { TxTitle, useArticlePath } from "@/lib/i18n";
import { Micro, CoinIcon, Pct, fmtPrice, Spark } from "@/components/layernav/primitives";
import { COIN_META } from "@/components/layernav/navConfig";
import { CardCTA, RegStatus, JURISDICTION_SHORT, homeTrack } from "./shared";

/* Per-card accent identity (Figma 230:3 Intel Cards): 1px stroke = linear
   gradient bottom→top, accent@1 → rgba(102,102,102,0). Fill stays #000000.
   Hover/focus glow (founder-approved) emanates from the accent stroke via
   --intel-glow (see index.css .intel-accent). */
const ACCENT_CARD = "intel-accent rounded-xl bg-transparent";
const accentStyle = (accent: string) => ({
  border: "1px solid transparent",
  background: `linear-gradient(#000000,#000000) padding-box, linear-gradient(to top, ${accent} 0%, rgba(102,102,102,0) 100%) border-box`,
  ["--intel-glow" as any]: `${accent}59`,
});

const RegulationRadarCard = ({ regs }: { regs: Regulation[] }) => (
  <div data-testid="regulation-radar-card" className={`${ACCENT_CARD} p-4 flex flex-col`} style={accentStyle("#8b5cff")}>
    <Micro className="flex items-center gap-1.5 !text-[#ab6bff]">
      <Scale className="h-3.5 w-3.5 text-[#ab6bff]" /> Regulation Radar
    </Micro>
    <div className="mt-2 flex-1 divide-y divide-[#1c212e]">
      {regs.slice(0, 2).map((r) => (
        <Link key={r.id} to={`/regulation/${r.slug}`} data-testid={`radar-row-${r.slug}`}
          onClick={() => homeTrack("regulation_radar", r.slug)}
          className="group block py-2.5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] font-bold text-[#A78BFA] bg-[#7C3AED]/10 border border-[#7C3AED]/40 rounded px-1.5 py-px shrink-0">
              {JURISDICTION_SHORT[r.countries?.[0]] || r.countries?.[0] || "—"}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-600 truncate">
              {r.agencies?.[0]}
            </span>
          </div>
          <p className="font-news text-[13px] font-semibold text-[#f2f5fc] group-hover:text-[#0dd6eb] transition-colors leading-snug line-clamp-2 mt-1.5">
            {r.title}
          </p>
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <RegStatus status={r.status} />
            <span className="font-mono text-[9px] text-[#7a8299] shrink-0">{timeAgo(r.published_at)}</span>
          </div>
        </Link>
      ))}
      {regs.length === 0 && <p className="text-sm text-gray-500 py-3">No tracked developments right now.</p>}
    </div>
    <CardCTA to="/regulation-tracker" testid="radar-card-cta" module="regulation_radar">View full radar</CardCTA>
  </div>
);

const MoverRow = ({ c }: { c: any }) => {
  const { data: detail } = useQuery({
    queryKey: ["nav-spark", c.symbol],
    queryFn: async () => (await api.get(`/markets/${c.symbol}?range=7d`)).data,
    staleTime: 5 * 60 * 1000,
  });
  const chart: number[] = (detail?.chart || []).map((p: any) => p.price);
  return (
    <Link to={`/coin/${c.symbol}`} data-testid={`mover-${c.symbol}`}
      onClick={() => homeTrack("top_movers", c.symbol)}
      className="flex items-center gap-2 rounded-lg px-2 py-1.5 -mx-2 hover:bg-white/[0.04] transition-colors">
      <CoinIcon symbol={c.symbol} size={22} />
      <span className="min-w-0 flex-1">
        <span className="block font-mono text-[12px] font-bold text-[#f2f5fc] leading-tight">{c.symbol}</span>
        <span className="block text-[9px] text-[#7a8299] truncate">
          {COIN_META[c.symbol]?.name || c.symbol}
        </span>
      </span>
      <Spark points={chart} up={c.change_24h >= 0} className="w-9 h-3.5 hidden sm:block shrink-0" />
      <span className="text-right shrink-0">
        <span className="block font-mono text-[12px] text-white leading-tight">{fmtPrice(c.price)}</span>
        <Pct v={c.change_24h} className="!text-[10px] font-bold" />
      </span>
    </Link>
  );
};

const TopMoversCard = () => {
  const { data } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    refetchInterval: 90000,
  });
  const movers = [...(data?.coins || [])]
    .sort((a: any, b: any) => Math.abs(b.change_24h) - Math.abs(a.change_24h))
    .slice(0, 4);
  return (
    <div data-testid="top-movers-card" className={`${ACCENT_CARD} p-4 flex flex-col`} style={accentStyle("#f5061c")}>
      <Micro className="flex items-center gap-1.5 !text-[#f5061c]">
        <Flame className="h-3.5 w-3.5 text-[#f5061c]" /> Top Movers · 24H
      </Micro>
      <div className="mt-3 flex-1 space-y-0.5">
        {movers.map((c: any) => <MoverRow key={c.symbol} c={c} />)}
        {movers.length === 0 && (
          <p className="font-mono text-[11px] text-gray-600 py-4">Market data temporarily unavailable</p>
        )}
      </div>
      <CardCTA to="/coin/BTC" testid="movers-card-cta" module="top_movers">View live charts</CardCTA>
    </div>
  );
};

const DailyBriefCard = ({ brief }: { brief: Article | null }) => {
  const toArticle = useArticlePath();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const tracked = useRef(false);
  if (!brief) return null;
  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else {
      a.play();
      setPlaying(true);
      if (!tracked.current) {
        tracked.current = true;
        api.post("/track/audio-play", { slug: brief.slug }).catch(() => {});
      }
    }
  };
  return (
    <div data-testid="daily-brief-card" className={`${ACCENT_CARD} p-4 flex flex-col relative overflow-hidden isolate`} style={accentStyle("#28d948")}>
      <div aria-hidden className="absolute -right-4 -bottom-5 h-28 w-[88px] rotate-6 rounded-lg border border-[#7C3AED]/25 bg-gradient-to-b from-[#7C3AED]/[0.12] to-transparent p-3 -z-10 pointer-events-none">
        <div className="h-1.5 w-9 rounded-sm bg-[#7C3AED]/50" />
        <div className="mt-2 space-y-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-px rounded bg-white/15" style={{ width: `${88 - i * 9}%` }} />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Micro className="!tracking-[0.2em] whitespace-nowrap !text-[#28d948]">The Daily Brief</Micro>
        <svg aria-hidden viewBox="0 0 54 20" className="ml-auto h-3.5 w-8 shrink-0 text-[#7C3AED]/50">
          {[3, 8, 5, 12, 7, 15, 10, 14, 7, 11, 4].map((h, i) => (
            <rect key={i} x={i * 5} y={10 - h / 2} width="2.5" height={h} rx="1.25" fill="currentColor" />
          ))}
        </svg>
      </div>
      <p className="text-[12px] text-gray-500 mt-2 leading-snug">
        Your 5-minute read on the onchain economy before your day starts.
      </p>
      <Link to={toArticle(brief.slug)} onClick={() => homeTrack("daily_brief", brief.slug)}
        className="group block mt-3 flex-1">
        <p className="font-news text-[14px] font-bold text-[#f2f5fc] group-hover:text-[#0dd6eb] transition-colors leading-snug line-clamp-2">
          <TxTitle slug={brief.slug} title={brief.title} />
        </p>
      </Link>
      <div className="flex items-center gap-3 mt-3">
        {brief.audio_url && (
          <>
            <button type="button" data-testid="daily-brief-play" onClick={toggle}
              aria-label={playing ? "Pause audio brief" : "Listen to today's Audio Brief"}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED] hover:bg-[#6b2fd6] text-white font-bold text-[11px] pl-2.5 pr-3.5 py-1.5 transition-colors shrink-0">
              {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3 ml-0.5" />}
              Listen
            </button>
            <audio ref={audioRef} src={`${process.env.REACT_APP_BACKEND_URL}${brief.audio_url}`}
              preload="none" onEnded={() => setPlaying(false)} />
          </>
        )}
        <span className="font-mono text-[9px] uppercase tracking-wider text-gray-600">
          Updated {timeAgo(brief.published_at)}
        </span>
      </div>
      <CardCTA to={toArticle(brief.slug)} testid="daily-brief-cta" module="daily_brief">Read today's Brief</CardCTA>
    </div>
  );
};

const PremiumIntelCard = ({ feature }: { feature: Article | null }) => {
  const { user } = useAuth();
  const toArticle = useArticlePath();
  if (!feature) return null;
  const isPremium = !!user?.is_premium;
  return (
    <div data-testid="premium-intel-card"
      className={`${ACCENT_CARD} p-4 flex flex-col relative overflow-hidden`} style={accentStyle("#f8c008")}>
      <div aria-hidden className="absolute -top-12 -right-12 h-40 w-40 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(248,192,8,0.14), transparent 70%)" }} />
      <div className="flex items-center justify-between">
        <Micro className="flex items-center gap-1.5 !tracking-[0.16em] !text-[#f8c008]">
          Premium Intelligence
        </Micro>
        <span className="font-mono text-[8px] font-bold uppercase tracking-widest bg-[#F8C008] text-black rounded px-1.5 py-0.5">
          Pro
        </span>
      </div>
      <Link to={toArticle(feature.slug)} onClick={() => homeTrack("premium_intel", feature.slug)}
        className="group block mt-3.5 flex-1">
        <p className="font-news text-[15px] font-bold text-[#f2f5fc] group-hover:text-[#0dd6eb] transition-colors leading-snug line-clamp-2">
          <TxTitle slug={feature.slug} title={feature.title} />
        </p>
        {(feature.summary_ai || feature.summary) && (
          <p className="text-[12px] text-gray-500 mt-2 leading-snug line-clamp-2">
            {feature.summary_ai || feature.summary}
          </p>
        )}
      </Link>
      <svg aria-hidden viewBox="0 0 120 26" preserveAspectRatio="none" className="mt-2 w-full h-6">
        <g fill="#F8C008" fillOpacity="0.22">
          {[8, 12, 6, 14, 9, 16, 11, 18, 13, 20, 15, 22].map((h, i) => (
            <rect key={i} x={i * 10 + 1} y={26 - h} width="5" height={h} rx="1" />
          ))}
        </g>
        <path d="M0 22 L12 18 L24 20 L36 13 L48 15 L60 9 L72 11 L84 6 L96 8 L108 3 L120 5"
          fill="none" stroke="#F8C008" strokeOpacity="0.55" strokeWidth="1.4" />
      </svg>
      {isPremium ? (
        <CardCTA to={toArticle(feature.slug)} testid="premium-card-cta" module="premium_intel">Read the analysis</CardCTA>
      ) : (
        <CardCTA to="/premium" testid="premium-card-cta" module="premium_intel">
          {user ? "Upgrade to unlock" : "Go Premium"}
        </CardCTA>
      )}
    </div>
  );
};

export const IntelligenceRow = ({ regs, brief, premiumFeature }: {
  regs: Regulation[]; brief: Article | null; premiumFeature: Article | null;
}) => (
  <section data-testid="intelligence-row"
    className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
    <RegulationRadarCard regs={regs} />
    <TopMoversCard />
    <DailyBriefCard brief={brief} />
    <PremiumIntelCard feature={premiumFeature} />
  </section>
);
